/**
 * Información del negocio.  (spec 038)
 *
 * Los mismos apartados del alta, menos catálogo. El WhatsApp se enseña (052).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { useSoloLectura } from '../../auth'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { api, ErrorApi } from '../../lib/api'
import type { FichaAlta } from '../../lib/altas'
import {
    autoradaDesdeAlta,
    fichaAltaDesdeVista,
    PASOS_OMITIDOS_INFORMACION,
    textoLineaWhatsapp,
    type ContactoVista,
    type InformacionRespuesta,
} from '../../lib/informacion'
import { PuestaEnMarcha } from '../altas/puesta-en-marcha'

const CONTACTO_VACIO: ContactoVista = {
    address: '',
    phone: '',
    email: '',
    parking: '',
    transport: '',
    website: '',
    extraInfo: '',
}

function Campo({
    id,
    label,
    children,
}: {
    id: string
    label: string
    children: React.ReactNode
}) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            {children}
        </div>
    )
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <section className="space-y-3 border-b border-neutral-800 pb-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{titulo}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>
        </section>
    )
}

export function InformacionNegocio() {
    const { accountId } = useParams()
    const id = Number(accountId)
    const qc = useQueryClient()
    const soloLectura = useSoloLectura()
    const [contacto, setContacto] = useState<ContactoVista>(CONTACTO_VACIO)
    const [fichaForm, setFichaForm] = useState<FichaAlta | null>(null)
    const [nombre, setNombre] = useState('')
    const [zona, setZona] = useState('')
    const [aviso, setAviso] = useState<string | null>(null)

    const info = useQuery({
        queryKey: ['informacion', id],
        queryFn: () => api.get<InformacionRespuesta>(`/business/${id}/informacion`),
        enabled: Number.isFinite(id) && id > 0,
    })

    useEffect(() => {
        if (!info.data) return
        setContacto({ ...CONTACTO_VACIO, ...info.data.contacto })
        setFichaForm(
            info.data.ficha
                ? {
                      ...fichaAltaDesdeVista(info.data.ficha),
                      ...(typeof info.data.contacto.nAdministradores === 'number'
                          ? { nAdministradores: info.data.contacto.nAdministradores }
                          : {}),
                  }
                : null,
        )
        setNombre(info.data.ficha?.identidad.name ?? '')
        setZona(info.data.ficha?.identidad.timezone ?? '')
    }, [info.data])

    const guardar = useMutation({
        mutationFn: async () => {
            const body: Record<string, string | number> = {}
            for (const [k, v] of Object.entries(contacto)) {
                if (k === 'nAdministradores') continue
                if (typeof v === 'string' && v.trim()) body[k] = v.trim()
            }
            if (fichaForm?.nAdministradores !== undefined) {
                body.nAdministradores = fichaForm.nAdministradores
            }
            const contactoRes = await api.put<{ accountId: number; campos: string[] }>(
                `/business/${id}/informacion/contacto`,
                body,
            )
            if (!fichaForm || !info.data?.ficha) {
                return { contacto: contactoRes, ficha: false as const }
            }
            await api.put<{ ok: true }>(`/business/${id}/informacion/ficha`, {
                ficha: autoradaDesdeAlta(fichaForm, info.data.ficha.profesionalesParaAsignacion),
                ...(nombre.trim() ? { clinicName: nombre.trim() } : {}),
                ...(zona.trim() ? { timezone: zona.trim() } : {}),
            })
            return { contacto: contactoRes, ficha: true as const }
        },
        onSuccess: (r) => {
            setAviso(
                r.ficha
                    ? 'Guardado. La ficha del asistente está al día.'
                    : `Guardado el contacto: ${r.contacto.campos.join(', ') || '(sin cambios)'}`,
            )
            void qc.invalidateQueries({ queryKey: ['informacion', id] })
            void qc.invalidateQueries({ queryKey: ['negocio-nombre', String(id)] })
        },
        onError: (e: Error) => {
            const extra =
                e instanceof ErrorApi && e.cuerpo && typeof e.cuerpo === 'object' && 'problemas' in e.cuerpo
                    ? (e.cuerpo as { problemas?: string[] }).problemas
                    : undefined
            setAviso(extra?.length ? `${e.message}: ${extra.join(' · ')}` : e.message)
        },
    })

    if (!Number.isFinite(id) || id < 1) {
        return <p className="text-sm text-neutral-500">Negocio no válido</p>
    }
    if (info.isLoading) {
        return <p className="text-sm text-neutral-500">Cargando…</p>
    }
    if (info.isError) {
        return <p className="text-sm text-amber-200/90">{info.error.message}</p>
    }

    const patchContacto = (k: keyof ContactoVista, v: string) => setContacto({ ...contacto, [k]: v })

    return (
        <div className="mx-auto w-full max-w-[1700px] space-y-6" data-testid="informacion-negocio">
            <div>
                <h1 className="text-lg font-semibold text-neutral-100">Información</h1>
                <p className="text-xs text-neutral-500">
                    Los mismos apartados del alta. Trabajadores, servicios y horarios están en{' '}
                    <Link to={`/business/${id}/catalogo`} className="text-sky-300 hover:underline">
                        Trabajadores y servicios
                    </Link>
                    .
                </p>
            </div>

            {aviso ? (
                <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200" data-testid="ficha-aviso">
                    {aviso}
                </p>
            ) : null}

            {!fichaForm ? (
                <p className="text-sm text-neutral-500">
                    {info.data?.motivoFicha ?? 'Este negocio no tiene ficha en el asistente.'}
                </p>
            ) : (
                <form
                    className="space-y-6"
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (soloLectura) return
                        guardar.mutate()
                    }}
                >
                    <PuestaEnMarcha
                        ficha={fichaForm}
                        onChange={setFichaForm}
                        nombreNegocio={nombre}
                        conPropuesta={false}
                        omitir={PASOS_OMITIDOS_INFORMACION}
                        extras={{
                            identidad: (
                                <Seccion titulo="Datos de aprovisionamiento">
                                    <Campo id="clinicName" label="Nombre del negocio">
                                        <Input
                                            id="clinicName"
                                            value={nombre}
                                            onChange={(e) => setNombre(e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="timezone" label="Zona horaria">
                                        <Input
                                            id="timezone"
                                            value={zona}
                                            onChange={(e) => setZona(e.target.value)}
                                        />
                                    </Campo>
                                </Seccion>
                            ),
                            reglas: (
                                <Seccion titulo="Horario de la agenda">
                                    <div className="md:col-span-2 xl:col-span-3">
                                        <p className="text-xs text-neutral-500">
                                            El horario no vive aquí: se corrige en{' '}
                                            <Link
                                                to={`/business/${id}/catalogo`}
                                                className="text-sky-300 hover:underline"
                                            >
                                                Trabajadores y servicios
                                            </Link>
                                            .
                                        </p>
                                    </div>
                                </Seccion>
                            ),
                            whatsapp: (
                                <Seccion titulo="Línea">
                                    <p
                                        className="text-sm text-neutral-100 md:col-span-2"
                                        data-testid="whatsapp-linea"
                                    >
                                        {textoLineaWhatsapp(info.data?.whatsapp?.e164)}
                                    </p>
                                </Seccion>
                            ),
                            contacto: (
                                <Seccion titulo="Contacto">
                                    <Campo id="address" label="Dirección">
                                        <Input
                                            id="address"
                                            value={contacto.address ?? ''}
                                            onChange={(e) => patchContacto('address', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="phone" label="Teléfono">
                                        <Input
                                            id="phone"
                                            value={contacto.phone ?? ''}
                                            onChange={(e) => patchContacto('phone', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="email" label="Correo">
                                        <Input
                                            id="email"
                                            type="email"
                                            value={contacto.email ?? ''}
                                            onChange={(e) => patchContacto('email', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="website" label="Web">
                                        <Input
                                            id="website"
                                            type="url"
                                            value={contacto.website ?? ''}
                                            onChange={(e) => patchContacto('website', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="parking" label="Aparcamiento">
                                        <Input
                                            id="parking"
                                            value={contacto.parking ?? ''}
                                            onChange={(e) => patchContacto('parking', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="transport" label="Cómo llegar">
                                        <Input
                                            id="transport"
                                            value={contacto.transport ?? ''}
                                            onChange={(e) => patchContacto('transport', e.target.value)}
                                        />
                                    </Campo>
                                    <Campo id="extraInfo" label="Info extra">
                                        <Input
                                            id="extraInfo"
                                            value={contacto.extraInfo ?? ''}
                                            onChange={(e) => patchContacto('extraInfo', e.target.value)}
                                        />
                                    </Campo>
                                </Seccion>
                            ),
                        }}
                    />
                    {!soloLectura && (
                    <div className="flex justify-end gap-3">
                        <Button type="submit" disabled={guardar.isPending}>
                            {guardar.isPending ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </div>
                    )}
                </form>
            )}
        </div>
    )
}
